import { sum } from 'lib/array/arrayUtils'

import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'

export const SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES = [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON]
export const DEFAULT_CHANNEL_MARKUP_VALUE = 1
const ROUND_UP_MULTIPLE = 10
const ALLOWED_UTM_SOURCE = ['impact_radius', 'google', 'facebook']

export type ChannelMarkupDetails = {
  markup: number
  isApplied: boolean
  /**
   * A v2 `fixed` markup's flat amount, in the campaign's currency. Set *instead of* `markup`
   * (which stays at the no-op `1`). Negative for a flat markdown (channel discount) — see
   * `channelMarkupV2MarkupToDetails`.
   */
  fixedAmount?: number
}

export const NO_CHANNEL_MARKUP: ChannelMarkupDetails = { markup: DEFAULT_CHANNEL_MARKUP_VALUE, isApplied: false }

/**
 * Round a numeric value up to the nearest multiple of 10.
 * Examples:
 * - 4198.95 -> 4200
 * - 3255.34 -> 3260
 * - 770     -> 770
 *
 * Accepts number or numeric string input to mirror floatify-style helpers.
 */
export function roundUpToNearestTen(value: number | string): number {
  const num = parseFloat(value as any)
  if (!Number.isFinite(num)) {
    // Mirror the behaviour of parseFloat-style helpers: return NaN (or +/-Infinity) as-is.
    return num
  }
  return Math.ceil(num / ROUND_UP_MULTIPLE) * ROUND_UP_MULTIPLE
}

function isOfferEligibleForChannelMarkup(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): boolean {
  if (!offerType || !channelMarkup) {
    return false
  }
  if (channelMarkup.channelMarkupBlacklist.includes(offerId)) {
    return false
  }
  return SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES.includes(offerType)
}

export function getChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const channelMarkupValue = channelMarkup!.channelMarkupValue
  const isApplied = !!channelMarkupValue && channelMarkupValue !== DEFAULT_CHANNEL_MARKUP_VALUE
  return {
    markup: isApplied ? channelMarkupValue : DEFAULT_CHANNEL_MARKUP_VALUE,
    isApplied,
  }
}

export function getMemberPriceChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!config.ENABLE_CHANNEL_MARKUP_FOR_MEMBER_PRICE) {
    return NO_CHANNEL_MARKUP
  }
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const luxplusMarkup = channelMarkup?.luxplusMarkupValue
  if (!luxplusMarkup) {
    return NO_CHANNEL_MARKUP
  }
  return { markup: luxplusMarkup, isApplied: true }
}

export function isAllowedChannelMarkupQueryParams(filters: App.ChannelMarkupFilters) {
  return ALLOWED_UTM_SOURCE.includes(filters.source)
}

export function applyChannelMarkupAndRoundUpToTen(value: number, markupDetails: ChannelMarkupDetails): number {
  if (!markupDetails.isApplied) {
    // No channel markup applied: return original price untouched.
    return value
  }

  if (markupDetails.fixedAmount != null) {
    // Callers pass 0 for a price the offer does not have (usually an absent member price,
    // `luxPlusPrice ?? 0`). A percentage leaves that 0 alone, so a flat amount must too.
    if (value <= 0) {
      return value
    }
    const markedValue = roundUpToNearestTen(value + markupDetails.fixedAmount)
    // A negative amount is a flat markdown (channel discount), which svc-order applies as a signed
    // surcharge. One larger than the price itself is a misconfigured campaign, not a free stay: leave
    // the base alone rather than quote zero or a negative price. svc-order still prices `base +
    // amount`, so such a campaign fails order creation on the price mismatch instead of charging it.
    if (markedValue <= 0) {
      return value
    }
    return markedValue
  }

  const updatedValue = value * markupDetails.markup
  // Markups and markdowns both round UP to the nearest ten. A small markdown can round to/above base
  // (intended). Cross-service: svc-order must round markdowns up and tolerate that, or order creation
  // rejects the client price (hotel_invalid_price).
  return roundUpToNearestTen(updatedValue)
}

/**
 * How much a LuxPlus+ channel markdown takes off a member price: the welcome saving a non-member
 * gets for joining LuxPlus+, on top of the usual member saving.
 *
 * Pass the unmarked price in the grouping the marked one was rounded in (one total, or one entry per
 * room). Each is rounded up to ten first, as the marked price was, so a flat A$100 markdown reads
 * A$100 rather than anything from A$90 to A$100. Anything under ten is rounding, not a markdown.
 */
export function getLuxPlusChannelMarkdown(
  unmarkedMemberPrice: number | Array<number>,
  markedMemberPrice: number,
  memberMarkupDetails: ChannelMarkupDetails,
): number {
  if (!memberMarkupDetails.isApplied || markedMemberPrice <= 0) {
    return 0
  }
  const unmarkedPrices = Array.isArray(unmarkedMemberPrice) ? unmarkedMemberPrice : [unmarkedMemberPrice]
  const markdown = sum(unmarkedPrices.map(roundUpToNearestTen)) - markedMemberPrice
  return markdown >= ROUND_UP_MULTIPLE ? markdown : 0
}

/**
 * Mark each room of a stay, then sum — the grouping checkout prices from.
 *
 * svc-order charges a `fixed` amount once per accommodation item and checkout builds one item per
 * room, so marking the combined total instead would quote `fixedAmount x (rooms - 1)` too little.
 */
export function applyChannelMarkupPerRoomAndRoundUpToTen(
  roomPrices: Array<number>,
  markupDetails: ChannelMarkupDetails,
): number {
  if (roomPrices.length <= 1) {
    return applyChannelMarkupAndRoundUpToTen(sum(roomPrices), markupDetails)
  }
  return sum(roomPrices.map(roomPrice => applyChannelMarkupAndRoundUpToTen(roomPrice, markupDetails)))
}

/**
 * Total a stay from its already-marked rows, so rows and total can never drift.
 *
 * Marking the combined base instead rounds once while the rows round individually, parting the two
 * by up to ten per extra room. Falls back to the base when un-marked or single-room (identical).
 */
export function totalMarkedRooms(
  markedRoomValues: Array<number>,
  unmarkedTotal: number,
  markupDetails: ChannelMarkupDetails,
): number {
  if (!markupDetails.isApplied || markedRoomValues.length <= 1) {
    return applyChannelMarkupAndRoundUpToTen(unmarkedTotal, markupDetails)
  }
  return sum(markedRoomValues)
}

/**
 * Whether a bedbank search tile should keep its own marked rate total instead of the search API's
 * `lowestPrice`.
 *
 * The search result carries one whole-party `lowestPrice`, and `hotelOfferMap` marks it once. But
 * svc-order charges a `fixed` amount once per accommodation item and checkout builds one item per
 * room, so `bedbankRateMap` marks each room and sums. For a multi-room stay the API figure is short
 * by `fixedAmount x (rooms - 1)` — the tile then quotes under the offer page and under the charge.
 * Whenever a fixed markup applies and we already hold a marked rate, that rate total wins.
 *
 * A percentage markup keeps the narrower rule this guard was written for (the API rounds property
 * fees into its markup base, mismatching svc-order), so live percentage tile prices do not move.
 */
export function shouldKeepMarkedRateOverSearchPrice(
  markupDetails: ChannelMarkupDetails,
  hasMarkedRate: boolean,
  propertyFees: number,
): boolean {
  if (!markupDetails.isApplied || !hasMarkedRate) {
    return false
  }
  return markupDetails.fixedAmount != null || propertyFees > 0
}

/**
 * Total a stay's member price from its already-marked rows, falling back to marking the total.
 *
 * A rate's member price is optional per row while its `totals` counterpart is not, so a rate can
 * carry a member total with only some — or none — of its rooms priced for members. Summing the
 * rows there quotes a member price short by every unpriced room, or zero, which reads as "no
 * member price" and silently drops the member fare. Only sum when every room carries one; with a
 * gap, mark the total itself, as the rate did before it had rows to sum.
 */
export function totalMarkedMemberRooms(
  markedRoomValues: Array<number | undefined>,
  unmarkedTotal: number,
  markupDetails: ChannelMarkupDetails,
): number {
  if (!markedRoomValues.every((value): value is number => !!value)) {
    return applyChannelMarkupAndRoundUpToTen(unmarkedTotal, markupDetails)
  }
  return totalMarkedRooms(markedRoomValues, unmarkedTotal, markupDetails)
}

/**
 * Mark a combined total that covers `roomCount` rooms but carries no per-room rows to mark
 * (the unified availability mapper's `combined` bucket).
 *
 * svc-order charges a `fixed` amount once per accommodation item and checkout builds one item per
 * room, so the total has to round up to ten *per room* and sum, exactly as
 * `applyChannelMarkupPerRoomAndRoundUpToTen` does for a rate that has rows. Scaling the amount
 * alone and rounding the combined figure once quotes up to ten per extra room under the charge.
 *
 * With no rows to split on, the base is divided evenly across the rooms. That is exact when the
 * rooms are priced alike and can differ by a few dollars when their occupancies are not; the
 * per-occupancy buckets remain the exact per-room view.
 *
 * Only a `fixed` markup takes this path. A percentage is left on the combined round-once
 * behaviour it ships with today, so rolling out fixed amounts cannot move a live percentage price.
 */
export function applyChannelMarkupToCombinedRoomsAndRoundUpToTen(
  combinedValue: number,
  markupDetails: ChannelMarkupDetails,
  roomCount: number,
): number {
  if (!markupDetails.isApplied || markupDetails.fixedAmount == null || roomCount <= 1 || combinedValue <= 0) {
    return applyChannelMarkupAndRoundUpToTen(combinedValue, markupDetails)
  }
  const perRoomBase = combinedValue / roomCount
  return applyChannelMarkupPerRoomAndRoundUpToTen(
    Array.from({ length: roomCount }, () => perRoomBase),
    markupDetails,
  )
}

/** {@link getLuxPlusChannelMarkdown} for a total marked by {@link applyChannelMarkupToCombinedRoomsAndRoundUpToTen}. */
export function getCombinedRoomsLuxPlusChannelMarkdown(
  unmarkedTotal: number,
  markedTotal: number,
  memberMarkupDetails: ChannelMarkupDetails,
  roomCount: number,
): number {
  const markedPerRoom = memberMarkupDetails.fixedAmount != null && roomCount > 1
  return getLuxPlusChannelMarkdown(
    markedPerRoom ? Array.from({ length: roomCount }, () => unmarkedTotal / roomCount) : unmarkedTotal,
    markedTotal,
    memberMarkupDetails,
  )
}

/**
 * Scale a markup to the rooms a combined total covers, for a total with no per-room rows to mark
 * (LPC's `combined` availability bucket). A percentage already scales and is returned untouched.
 */
export function scaleChannelMarkupToRooms(
  markupDetails: ChannelMarkupDetails,
  roomCount: number,
): ChannelMarkupDetails {
  if (markupDetails.fixedAmount == null || roomCount <= 1) {
    return markupDetails
  }
  return { ...markupDetails, fixedAmount: markupDetails.fixedAmount * roomCount }
}

/**
 * Mark a tax/fee breakdown line. A percentage scales it with the price; a `fixed` amount is added
 * to the price only, since marking the tax line too would overstate the tax.
 */
export function applyChannelMarkupToTaxesAndFees(value: number, markupDetails: ChannelMarkupDetails): number {
  if (markupDetails.fixedAmount != null) {
    return value
  }
  return applyChannelMarkupAndRoundUpToTen(value, markupDetails)
}

/**
 * Re-derive a rate's marked selling price from its base, using the markup carried on the totals.
 *
 * `mapRateTotals` marks `displayPrice` excluding property fees and absorbs the surcharge into
 * `sellingPrice`; because `displayPrice` is `sellingPrice` plus property fees, that surcharge
 * reduces to marking the selling price itself. A rate repriced mid-booking (svc-bedbank quotes an
 * unmarked hotel price) must be marked again the same way, or the pair drifts and the customer is
 * shown or charged the wrong price.
 */
export function markPropertyRateSellingPrice(
  baseSellingPrice: number,
  channelMarkup: App.ChannelMarkupDetails | undefined,
): number {
  if (!channelMarkup) return baseSellingPrice
  return applyChannelMarkupAndRoundUpToTen(baseSellingPrice, channelMarkup)
}
